import { Button, Modal, Input, message } from "antd";
import { IUnfilledZoneData } from "models/interfaces";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  getCurrentDate,
  getCurrentTime,
  getCustomDate,
  getCustomTime,
} from "utils/formatTimePeriod";
import { FormatCurrencyUnit } from "utils/formatCurrency";
import { showConfirm } from "utils/commonConfirmation/confirmationPopup";
import { HouseKeepingService } from "services/SkyboxServices";
import { useRbac } from "features/rbac/rbac";
import { AppSetting } from "utils/appSettings";
import { twoDecimalPlacesNumber } from "utils/commonFunction";

const { TextArea } = Input;

const CancelInvoiceModal: React.FC<{
  isModalVisible: boolean;
  setIsModalVisible: Function;
  setIsCancelPending: Function;
  modalData: IUnfilledZoneData;
}> = ({ isModalVisible, setIsModalVisible, setIsCancelPending, modalData }) => {
  const { auth0UserDetails } = useRbac();
  const [defaultInvoiceCreditNote, setDefaultInvoiceCreditNote] = useState(
    `TB 2.0: Canceled show credit note on ${getCurrentDate(
      "MMM DD, YYYY"
    )} at ${getCurrentTime()} EST against PO Number: ${
      modalData?.purchase_id?.join(', ')
    } and PO Line ID: ${modalData?.purchase_line_id?.join(', ')}, Qty = ${modalData?.quantity}, Unit Price = ${modalData?.unit_ticket_sales}`
  );

  const [defaultPoCreditNote, setDefaultPoCreditNote] = useState(
    `TB 2.0: Canceled show credit note on ${getCurrentDate(
      "MMM DD, YYYY"
    )} at ${getCurrentTime()} EST against Invoice Number: ${
      modalData?.invoice_id
    } and Invoice Line ID: ${modalData?.invoice_line_id}, Qty = ${modalData?.quantity}, Unit Cost = ${modalData?.unit_cost}`
  );

  useEffect(()=>{
    setDefaultPoCreditNote(`TB 2.0: Canceled show credit note on ${getCurrentDate(
      "MMM DD, YYYY"
    )} at ${getCurrentTime()} EST against Invoice Number: ${
      modalData?.invoice_id
    } and Invoice Line ID: ${modalData?.invoice_line_id}, Qty = ${modalData?.quantity}, Unit Cost = ${modalData?.unit_cost}`);
    setDefaultInvoiceCreditNote(`TB 2.0: Canceled show credit note on ${getCurrentDate(
      "MMM DD, YYYY"
    )} at ${getCurrentTime()} EST against PO Number: ${
      modalData?.purchase_id?.join(', ')
    } and PO Line ID: ${modalData?.purchase_line_id?.join(', ')}, Qty = ${modalData?.quantity}, Unit Price = ${modalData?.unit_ticket_sales}`);
  }, [modalData]);

  const [invoiceCreditNote, setInvoiceCreditNote] = useState<string|null>(null);
  const [poCreditNote, setPoCreditNote] = useState<string|null>(null);

  const cancelHandler = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    e.preventDefault();
    setIsModalVisible(false);
    message.warning({content: "Operation cancelled by user", duration: 5, className: "toastMsg savedFailed"});
  };

  const cancelInvoiceCancelHandler = (_data: IUnfilledZoneData) => {
    message.warning({content: "Operation cancelled by user", duration: 5, className: "toastMsg savedFailed"});
  }

  const cancelInvoice = async (data: IUnfilledZoneData) => {
    const body = {
      invoiceId: data?.invoice_id,
      invLineId: data?.invoice_line_id,
      purchaseId: data?.purchase_id,
      poLineId: data?.purchase_line_id,
      quantity: data?.quantity,
      targetUrl: "",
      poCreditNote: poCreditNote && poCreditNote.trim().length > 0? poCreditNote : defaultPoCreditNote,
      invoiceCreditNote: invoiceCreditNote && invoiceCreditNote.trim().length > 0 ? invoiceCreditNote : defaultInvoiceCreditNote,
      invoiceAmount: data?.ticket_sales * -1,
      invoiceUnitPrice: data?.unit_ticket_sales,
      purchaseAmount: data?.ticket_cost * -1,
      purchaseUnitCost: data?.unit_cost,
      lineType: data?.line_type,
      lineItemType: data?.line_item_type,
      mailTo: auth0UserDetails?.email || '',
      isFromUnfilled: true
    };
    try {
      console.log(body);
      const res = await new HouseKeepingService().cancelInvoice(body);
      if(res?.status === 200) {
        setIsCancelPending(true);
        message.success({content: "Invoice Cancellation in progress.", duration: 5, className: "toastMsg savedSuccess"});
      }
      else {
        message.warning({content: "Failed to cancel invoice", duration: 5, className: "toastMsg savedFailed"});
      }
    } catch (error) {
      message.warning({content: "Failed to cancel invoice", duration: 5, className: "toastMsg savedFailed"});
    }  
  }

  const confirmHandler = () => {
    setIsCancelPending(false);
    const header = <span>Are you sure you want to <strong>cancel</strong> this invoice?</span>;
    const body = (
      <div>
        <div className="delMessageBody">
          <p>Invoice Line ID: {modalData?.invoice_line_id.toString()}</p>
          <p>Purchase Line ID: {modalData?.purchase_line_id.toString()}</p>
        </div>
        <div className="smlErrorTxt">This action cannot be undone.</div>
      </div>
    );
    showConfirm(header, body, modalData, cancelInvoice, cancelInvoiceCancelHandler);
    setInvoiceCreditNote(null);
    setPoCreditNote(null);
    setIsModalVisible(false);
  };

  return (
    <React.Fragment>
      {modalData && (
        <Modal
          className="cancelInvoicePurchaseOrderModal"
          closable={false}
          width={1008}
          title={false}
          footer={false}
          visible={isModalVisible}
          destroyOnClose={true}
        >
          <div className="modalCustomBody">
            <h3>Cancel Invoice &amp; Purchase Order</h3>
            <div className="inputGroup">
              <label>
                Invoice No:{" "}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={
                    AppSetting.INVOICE_ID_URL +
                    modalData?.invoice_id
                  }
                >
                  {modalData?.invoice_id}
                </a>
              </label>
              <div
                className="customTbl scroll-y scroll-x fixedHeaderFooter"
                style={{ maxHeight: "calc(100vh - 220px)" }}
              >
                <table className="main-table fixedHeaderFooter">
                  <thead>
                    <tr>
                      <th className="text-left">EVENT</th>
                      <th className="text-right lowSeatCol">LOW SEAT</th>
                      <th className="text-right highSeatCol">HIGH SEAT</th>
                      <th className="text-right unitConstCol">UNIT COST</th>
                      <th className="text-right totalCostCol">TOTAL COST</th>
                      <th className="text-right qtyCol">QTY</th>
                      <th className="text-right unitSaleCol">UNIT SALE</th>
                      <th className="text-right totalSaleCol">TOTAL SALE</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <div>
                          <a
                            className="eventNameVenue text-capitalize"
                            target="_blank"
                            rel="noreferrer"
                            href={
                              AppSetting.EVENT_URL +
                              modalData?.event_id
                            }
                          >
                            <strong>{modalData?.event}</strong>
                          </a>
                          <br />
                          {getCustomDate(
                            modalData?.event_date,
                            "ddd, MMM DD, YYYY"
                          )}
                          <br />
                          {getCustomTime(
                            `${modalData?.event_time}`,
                            "hh:mm A"
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="text-right">
                          {modalData?.seats?.split(",")[0]}
                        </div>
                      </td>
                      <td>
                        <div className="text-right">
                          {
                            modalData?.seats?.split(",")[
                              modalData?.seats?.split(",").length - 1
                            ]
                          }
                        </div>
                      </td>
                      <td>
                        <div className="text-right">
                          {FormatCurrencyUnit(
                            twoDecimalPlacesNumber(modalData?.unit_cost),
                            modalData?.currency_code
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="text-right">
                          {FormatCurrencyUnit(
                            twoDecimalPlacesNumber(modalData?.ticket_cost),
                            modalData?.currency_code
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="text-right">{modalData?.quantity}</div>
                      </td>
                      <td>
                        <div className="text-right">
                          {FormatCurrencyUnit(
                            twoDecimalPlacesNumber(modalData?.unit_ticket_sales),
                            modalData?.currency_code
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="text-right">
                          {FormatCurrencyUnit(
                            twoDecimalPlacesNumber(modalData?.ticket_sales),
                            modalData?.currency_code
                          )}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="inputGroup">
              <label>Invoice Credit Note</label>
              <div
                className="customTbl scroll-y scroll-x fixedHeaderFooter"
                style={{ maxHeight: "calc(100vh - 220px)" }}
              >
                <table className="main-table fixedHeaderFooter">
                  <thead>
                    <tr>
                      <th className="text-left">INVOICE ID</th>
                      <th className="text-left">INVOICE LINE ID</th>
                      <th className="text-left descCol">DESCRIPTION</th>
                      <th className="text-right qtyCol">QTY</th>
                      <th className="text-right">UNIT COST</th>
                      <th className="text-right">AMOUNT</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <div>
                          <a
                            className="eventNameVenue"
                            target="_blank"
                            rel="noreferrer"
                            href={
                              AppSetting.INVOICE_ID_URL +
                              modalData?.invoice_id
                            }
                          >
                            <strong>{modalData?.invoice_id}</strong>
                          </a>
                        </div>
                      </td>
                      <td>
                        <div>
                          <strong>{modalData?.invoice_line_id}</strong>
                        </div>
                      </td>
                      <td>
                        <div className="">
                          <TextArea
                            className="desc-textarea"
                            defaultValue={defaultInvoiceCreditNote}
                            onChange={(e)=>setInvoiceCreditNote(e.target.value)}
                          />
                        </div>
                      </td>
                      <td>
                        <div className="text-right">{modalData.quantity}</div>
                      </td>
                      <td>
                        <div className="text-right">{FormatCurrencyUnit(twoDecimalPlacesNumber(0),modalData?.currency_code)}</div>
                      </td>
                      <td>
                        <div className="text-right">{FormatCurrencyUnit(twoDecimalPlacesNumber(0),modalData?.currency_code)}</div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="inputGroup">
              <label>PO Credit Note</label>
              <div
                className="customTbl scroll-y scroll-x fixedHeaderFooter"
                style={{ maxHeight: "calc(100vh - 220px)" }}
              >
                <table className="main-table fixedHeaderFooter">
                  <thead>
                    <tr>
                      <th className="text-left">PO#</th>
                      <th className="text-left">PURCHASE LINE ID</th>
                      <th className="text-left descCol">DESCRIPTION</th>
                      <th className="text-right qtyCol">QTY</th>
                      <th className="text-right">UNIT COST</th>
                      <th className="text-right">AMOUNT</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <div>
                          <a
                            className="eventNameVenue"
                            target="_blank"
                            rel="noreferrer"
                            href={
                              AppSetting.PURCHASE_ID_URL +
                              modalData?.purchase_id
                            }
                          >
                            <strong>{modalData?.purchase_id}</strong>
                          </a>
                        </div>
                      </td>
                      <td>
                        <div>
                          <strong>{modalData?.purchase_line_id}</strong>
                        </div>
                      </td>
                      <td>
                        <div className="">
                          <TextArea
                            className="desc-textarea"
                            defaultValue={defaultPoCreditNote}
                            onChange={(e)=>setPoCreditNote(e.target.value)}
                          />
                        </div>
                      </td>
                      <td>
                        <div className="text-right">{modalData?.quantity}</div>
                      </td>
                      <td>
                        <div className="text-right">{FormatCurrencyUnit(twoDecimalPlacesNumber(0),modalData?.currency_code)}</div>
                      </td>
                      <td>
                        <div className="text-right">{FormatCurrencyUnit(twoDecimalPlacesNumber(0),modalData?.currency_code)}</div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="modalCustomFooter">
            <Link
              to="#/"
              className="discardBtn"
              onClick={(e) => cancelHandler(e)}
            >
              CANCEL
            </Link>
            <Button className="btnOk" onClick={confirmHandler}>CONFIRM CANCELLATION</Button>
          </div>
        </Modal>
      )}
    </React.Fragment>
  );
};

export default CancelInvoiceModal;
